import * as React from "react";

import { Button } from "@/components/ui/button";
import { isRunningStandalonePwa } from "@/utils/pwa";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

const STORAGE_KEY = "pwa-install-banner-dismissed";

const isIosDevice = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

export function PwaInstallBanner() {
  const [isVisible, setIsVisible] = React.useState(false);
  const [platform, setPlatform] = React.useState<"android" | "ios" | null>(null);
  const deferredPromptRef = React.useRef<BeforeInstallPromptEvent | null>(null);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (isRunningStandalonePwa()) {
      return;
    }

    const hasDismissed = window.localStorage.getItem(STORAGE_KEY) === "true";
    if (hasDismissed) {
      return;
    }

    let isPageLoaded = document.readyState === "complete";
    let pendingShow = false;
    let pendingPlatform: "android" | "ios" | null = null;

    const showBannerIfReady = (plt: "android" | "ios") => {
      if (isPageLoaded) {
        setPlatform(plt);
        setIsVisible(true);
      } else {
        pendingShow = true;
        pendingPlatform = plt;
      }
    };

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      deferredPromptRef.current = event as BeforeInstallPromptEvent;
      showBannerIfReady("android");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    if (isIosDevice()) {
      showBannerIfReady("ios");
    }

    const handleLoad = () => {
      isPageLoaded = true;
      if (pendingShow && pendingPlatform) {
        setPlatform(pendingPlatform);
        setIsVisible(true);
      }
    };

    if (!isPageLoaded) {
      window.addEventListener("load", handleLoad);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("load", handleLoad);
    };
  }, []);

  const handleInstall = React.useCallback(async () => {
    const deferredPrompt = deferredPromptRef.current;
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice;

    if (choiceResult.outcome === "accepted") {
      setIsVisible(false);
      window.localStorage.setItem(STORAGE_KEY, "true");
    }
  }, []);

  const handleDismiss = React.useCallback(() => {
    setIsVisible(false);
    window.localStorage.setItem(STORAGE_KEY, "true");
  }, []);

  if (!isVisible || !platform) {
    return null;
  }

  return (
    <div className="safe-fixed-bottom fixed inset-x-0 bottom-0 z-[350] mx-auto w-full max-w-5xl px-4">
      <div className="rounded-lg border border-border bg-background/95 p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/75">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Instala Comuni Home como app</p>
            {platform === "android" ? (
              <div className="space-y-1">
                <p>
                  Añade Comuni Home a tu pantalla de inicio para acceder rápidamente. Pulsa en
                  "Instalar" y sigue las instrucciones del sistema.
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                <p>
                  Añade Comuni Home a tu pantalla de inicio para tener una experiencia similar a una
                  app nativa.
                </p>
                <p>
                  1. Pulsa el botón <span className="font-medium">Compartir</span> en Safari.
                </p>
                <p>2. Selecciona <span className="font-medium">Añadir a pantalla de inicio</span>.</p>
                <p>3. Confirma con <span className="font-medium">Añadir</span>.</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 self-end md:self-center">
            {platform === "android" && (
              <Button onClick={handleInstall}>Instalar</Button>
            )}
            <Button variant="outline" onClick={handleDismiss}>
              Ahora no
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PwaInstallBanner;
